import { createSlice, isAnyOf } from '@reduxjs/toolkit'

import {
  addBusinessEmail,
  getBusinessEmails,
  removeBusinessEmail,
  setConnectBusinessEmailStep,
  updateBusinessEmail,
  verifyBusinessDomain,
} from 'apps/email/actions/businessDomainActions'
import { EmailState } from 'apps/email/emailInterfaces'
import { FetchStatus } from 'apps/sms/smsInterfaces'
import { BusinessEmail } from 'shared/api/common/schemas/email'

export const initialState: EmailState = {
  businessEmails: [],
  businessEmailsFetchStatus: FetchStatus.NOT_FETCHED,
  connectBusinessEmailStep: null,
  connectBusinessEmailId: null,
}

const setPreferredEmail = (
  businessEmails: BusinessEmail[],
  businessEmailId: number | null,
): BusinessEmail[] => {
  return businessEmails.map((businessEmail) => {
    const isPreferred = businessEmail.id === businessEmailId
    return { ...businessEmail, is_default: isPreferred }
  })
}

export const emailSlice = createSlice({
  name: 'email',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(removeBusinessEmail.fulfilled, (state, action) => {
      const email = action.meta.arg
      const removedBusinessEmail = state.businessEmails.find(
        (businessEmail) => businessEmail.email === email,
      )
      let businessEmails = state.businessEmails.filter(
        (businessEmail) => businessEmail.email !== email,
      )
      if (removedBusinessEmail?.is_default) {
        businessEmails = setPreferredEmail(businessEmails, null)
      }

      return { ...state, businessEmails }
    })

    builder.addCase(verifyBusinessDomain.fulfilled, (state, action) => {
      const { domain } = action.payload
      const businessEmails = state.businessEmails.map((businessEmail) =>
        businessEmail.domain.id === domain.id ? { ...businessEmail, domain } : businessEmail,
      )
      return { ...state, businessEmails }
    })

    builder.addCase(getBusinessEmails.pending, (state) => {
      return { ...state, businessEmailsFetchStatus: FetchStatus.FETCHING }
    })

    builder.addCase(getBusinessEmails.fulfilled, (state, action) => {
      const businessEmails = action?.payload?.emails
      return { ...state, businessEmails, businessEmailsFetchStatus: FetchStatus.FETCHED }
    })

    builder.addCase(getBusinessEmails.rejected, (state) => {
      return { ...state, businessEmailsFetchStatus: FetchStatus.NOT_FETCHED }
    })

    builder.addCase(setConnectBusinessEmailStep, (state, action) => {
      return {
        ...state,
        connectBusinessEmailStep: action?.payload?.step ?? null,
        connectBusinessEmailId: action?.payload?.emailId ?? null,
      }
    })

    builder.addMatcher(
      isAnyOf(addBusinessEmail.fulfilled, updateBusinessEmail.fulfilled),
      (state, action) => {
        const businessEmail = action.payload?.email
        let businessEmails = [
          ...state.businessEmails.filter((e) => e.email !== businessEmail.email),
          businessEmail,
        ]
        if (businessEmail?.is_default) {
          businessEmails = setPreferredEmail(businessEmails, businessEmail?.id)
        }

        return {
          ...state,
          businessEmails,
        }
      },
    )
  },
})

export const { reducer } = emailSlice
